import { Modal, BlockStack, Text, List, Divider } from "@shopify/polaris";
import { useTranslation } from "../../hooks/useTranslation";

interface DocumentationModalProps {
  open: boolean;
  onClose: () => void;
}

export function DocumentationModal({ open, onClose }: DocumentationModalProps) {
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("footer.documentation")}
      primaryAction={{
        content: t("common.close") || "Close",
        onAction: onClose,
      }}
      size="large"
    >
      <Modal.Section>
        <BlockStack gap="400">
          <Text as="h2" variant="headingMd">
            Getting Started with myDATA Comply
          </Text>

          <Text as="p" variant="bodyMd">
            myDATA Comply automates the submission of Greek delivery notes (παραστατικά αποστολής) to the AADE myDATA system directly from your Shopify orders.
          </Text>

          <Divider />

          <Text as="h2" variant="headingMd">
            Step 1: Configure myDATA Credentials
          </Text>

          <BlockStack gap="300">
            <Text as="p" variant="bodyMd">
              Navigate to the <Text as="span" fontWeight="semibold">Settings</Text> tab and enter:
            </Text>

            <List type="number">
              <List.Item>
                <Text as="span" fontWeight="semibold">myDATA User ID:</Text> Your AADE user ID for myDATA API access
              </List.Item>
              <List.Item>
                <Text as="span" fontWeight="semibold">Subscription Key:</Text> Your myDATA API subscription key (Ocp-Apim-Subscription-Key)
              </List.Item>
              <List.Item>
                <Text as="span" fontWeight="semibold">Environment:</Text> Choose "Sandbox" for testing or "Production" for real submissions
              </List.Item>
            </List>

            <Text as="p" variant="bodyMd" tone="subdued">
              <Text as="span" fontWeight="semibold">Tip:</Text> Start with Sandbox environment to test the integration before submitting real delivery notes.
            </Text>
          </BlockStack>

          <Divider />

          <Text as="h2" variant="headingMd">
            Step 2: Enter Business Information
          </Text>

          <BlockStack gap="300">
            <Text as="p" variant="bodyMd">
              Fill in your business details (all fields required):
            </Text>

            <List type="bullet">
              <List.Item><Text as="span" fontWeight="semibold">VAT Number (ΑΦΜ):</Text> Your 9-digit Greek tax identification number</List.Item>
              <List.Item><Text as="span" fontWeight="semibold">Company Name:</Text> Your registered business name</List.Item>
              <List.Item><Text as="span" fontWeight="semibold">Address:</Text> Business street address</List.Item>
              <List.Item><Text as="span" fontWeight="semibold">City:</Text> Business city</List.Item>
              <List.Item><Text as="span" fontWeight="semibold">Postal Code:</Text> 5-digit postal code</List.Item>
              <List.Item><Text as="span" fontWeight="semibold">Logo URL (optional):</Text> Company logo for PDF branding</List.Item>
            </List>

            <Text as="p" variant="bodyMd">
              This information will appear as the "issuer" on all delivery notes submitted to myDATA.
            </Text>
          </BlockStack>

          <Divider />

          <Text as="h2" variant="headingMd">
            Step 3: Submit Delivery Notes
          </Text>

          <BlockStack gap="300">
            <Text as="p" variant="bodyMd">
              Once configured, you can submit delivery notes from Shopify orders:
            </Text>

            <Text as="h3" variant="headingSm" fontWeight="semibold">
              Method 1: From Shopify Admin
            </Text>
            <List type="number">
              <List.Item>Open any order in Shopify Admin</List.Item>
              <List.Item>Click the "Submit to myDATA" button in the order actions</List.Item>
              <List.Item>Confirm the submission</List.Item>
              <List.Item>View the result (success or error) directly on the order</List.Item>
            </List>

            <Text as="h3" variant="headingSm" fontWeight="semibold">
              Method 2: From App Dashboard
            </Text>
            <List type="number">
              <List.Item>Go to the Dashboard tab</List.Item>
              <List.Item>View your recent orders</List.Item>
              <List.Item>Click "Submit" next to any order</List.Item>
              <List.Item>Track submission status in the Submissions tab</List.Item>
            </List>
          </BlockStack>

          <Divider />

          <Text as="h2" variant="headingMd">
            Understanding Quotas and Subscriptions
          </Text>

          <BlockStack gap="300">
            <Text as="h3" variant="headingSm" fontWeight="semibold">
              Free Trial
            </Text>
            <Text as="p" variant="bodyMd">
              New installations include <Text as="span" fontWeight="semibold">7 free submissions</Text>. No credit card required.
            </Text>

            <Text as="h3" variant="headingSm" fontWeight="semibold">
              Paid Plan
            </Text>
            <Text as="p" variant="bodyMd">
              After exhausting your free quota, subscribe to a paid plan: <Text as="span" fontWeight="semibold">Starter (€9.99/month, 30 submissions), Professional (€14.99/month, 60 submissions) or Growth (€29.99/month, 300 submissions)</Text>. Billing occurs through Shopify's payment system every 30 days.
            </Text>

            <Text as="h3" variant="headingSm" fontWeight="semibold">
              Quota Tracking
            </Text>
            <Text as="p" variant="bodyMd">
              Your current quota usage is displayed on the Dashboard. Quotas reset at the start of each billing cycle.
            </Text>
          </BlockStack>

          <Divider />

          <Text as="h2" variant="headingMd">
            Frequently Asked Questions
          </Text>

          <BlockStack gap="300">
            <Text as="h3" variant="headingSm" fontWeight="semibold">
              Q: What type of document does this app submit?
            </Text>
            <Text as="p" variant="bodyMd">
              A: The app submits "Delivery Notes" (Δελτίο Αποστολής) with invoice type 7.1 to myDATA. These are required by Greek tax law for shipments of goods.
            </Text>

            <Text as="h3" variant="headingSm" fontWeight="semibold">
              Q: Can I cancel a submitted delivery note?
            </Text>
            <Text as="p" variant="bodyMd">
              A: Yes, you can cancel (ακύρωση) a delivery note from the Submissions tab. Cancellation submits a reversal document to myDATA.
            </Text>

            <Text as="h3" variant="headingSm" fontWeight="semibold">
              Q: Where can I find the QR code and myDATA MARK?
            </Text>
            <Text as="p" variant="bodyMd">
              A: After successful submission, view the Submissions tab to see the MARK, UID, and QR code URL. You can also download a PDF with the QR code embedded.
            </Text>

            <Text as="h3" variant="headingSm" fontWeight="semibold">
              Q: What happens to customer data?
            </Text>
            <Text as="p" variant="bodyMd">
              A: Customer names and shipping addresses are sent to myDATA as required by Greek tax law. Review our Privacy Policy for full details on data handling.
            </Text>

            <Text as="h3" variant="headingSm" fontWeight="semibold">
              Q: Can I test without submitting real documents?
            </Text>
            <Text as="p" variant="bodyMd">
              A: Yes! Use the Sandbox environment in Settings. Sandbox submissions go to the myDATA test environment and won't affect your real tax records.
            </Text>

            <Text as="h3" variant="headingSm" fontWeight="semibold">
              Q: What if I get an error during submission?
            </Text>
            <Text as="p" variant="bodyMd">
              A: Error messages are displayed in the Submissions tab. Common issues include invalid credentials, missing business information, or myDATA API errors. Check the Support section for troubleshooting.
            </Text>
          </BlockStack>

          <Divider />

          <Text as="h2" variant="headingMd">
            Need More Help?
          </Text>

          <Text as="p" variant="bodyMd">
            Can't find what you're looking for? Contact support at lefteris@weam.gr or use the Support section for troubleshooting tips.
          </Text>

          <Divider />

          <Text as="p" variant="bodySm" tone="subdued">
            Last updated: January 2026
          </Text>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
